import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";

import * as DTO from "../../../interfaces";
import { VideoProjectEditorCardRecord, VideoProjectEditorCard } from "@/components";
import { AppContext } from "@/providers";

import { UsersService, VideoProjectServices } from "@/services";
import { isEqual, debounce, isEmpty, startCase, toLower } from "lodash";
import { extractInitials } from "@/utils";
import { Svg } from "@/assets";
import { PageWrapper } from "@/components";
import { Avatar, Badge, Checkbox, Combobox, Input, InputGroup, Pagination, Skeleton } from "@/components/ui";
import { Search } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

interface AssigneeOption {
  key: number;
  text: string;
  value: number;
  avatarUrl?: string;
}

const VideoProjectEditorCardSkeleton = () => (
  <div className="block w-[150px] bg-[#fafafa] rounded-[6.3px] border border-[#d4d4d4] overflow-hidden text-sm">
    <Skeleton className="w-full h-[110px] rounded-none bg-[#eaeaf0]" />
    <div className="p-2.5 flex flex-col gap-1.5">
      <Skeleton className="h-3.5 w-11/12" />
      <Skeleton className="h-3.5 w-8/12" />
      <Skeleton className="h-3 w-5/12 mt-1" />
      <Skeleton className="h-3 w-7/12" />
      <Skeleton className="h-3 w-6/12" />
    </div>
  </div>
);

const estimatePageSize = (): number => {
  if (typeof window === "undefined") return 21;

  const width = window.innerWidth;
  let estimatedContentWidth = width;

  if (width >= 1024) {
    // Desktop: Sidebar (230px) + Page margins (100px)
    estimatedContentWidth = width - 330;
  } else if (width >= 768) {
    // Tablet: Sidebar (230px) + Page margins (100px)
    estimatedContentWidth = width - 330;
  } else {
    // Mobile: No Sidebar + Page margins (32px)
    estimatedContentWidth = width - 32;
  }

  if (estimatedContentWidth <= 0) return 21;

  const cardWidth = 150;
  const gap = 16;
  const cardsPerRow = Math.max(1, Math.floor((estimatedContentWidth + gap) / (cardWidth + gap)));
  return cardsPerRow * 3;
};

const EditingQueueListFC: React.FC<RouteComponentProps> = () => {
  const { userContext } = React.useContext(AppContext);
  const currentUserId = userContext!.account.userId;

  const [searchTerm, setSearchTerm] = React.useState<string | null>(new URLSearchParams(location.search).get("search"));
  const statusValues = Object.values(DTO.VideoProjectStatus);
  const statusOptions = statusValues.map(status => ({ key: status, text: startCase(toLower(status)), value: status }));
  const [selectedStatuses, setSelectedStatuses] = React.useState<DTO.VideoProjectStatus[]>(
    localStorage
      .getItem("editingQueueList-selectedStatuses")
      ?.split("|")
      ?.map(el => el as DTO.VideoProjectStatus)
      ?.filter(el => statusValues.includes(el)) || [DTO.VideoProjectStatus.SUBMITTED_FOR_EDITING]
  );
  const [selectedAssignees, setSelectedAssignees] = React.useState<number[]>(
    localStorage
      .getItem("editingQueueList-selectedAssignees")
      ?.split("|")
      ?.map(el => parseInt(el))
      ?.filter(isFinite) || []
  );
  const [includeDemoProjects, setIncludeDemoProjects] = React.useState<boolean>(
    localStorage.getItem("includeDemoProjects") === "true"
  );

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState<number>(estimatePageSize);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updatePageSize = () => {
      const width = el.clientWidth;
      if (width > 0) {
        const cardWidth = 150;
        const gap = 16;
        const cardsPerRow = Math.max(1, Math.floor((width + gap) / (cardWidth + gap)));
        const targetPageSize = cardsPerRow * 3;
        setPageSize(prevSize => (prevSize !== targetPageSize ? targetPageSize : prevSize));
      }
    };

    updatePageSize();

    const debouncedUpdate = debounce(updatePageSize, 150);

    const observer = new ResizeObserver(() => {
      debouncedUpdate();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      debouncedUpdate.cancel();
    };
  }, []);

  const assignees = React.useMemo(() => {
    return selectedAssignees.length > 0 ? selectedAssignees.map(userId => (userId === -1 ? null : userId)) : undefined;
  }, [selectedAssignees]);

  const filters = React.useMemo<DTO.SearchVideoProjectFilter>(() => {
    return {
      status: selectedStatuses,
      searchTerm,
      includeDemoProjects,
      assignees,
    };
  }, [selectedStatuses, searchTerm, includeDemoProjects, assignees]);

  // Video projects query - fresh every time
  const { data: videoProjectsData, isFetching: loadingVideoProjects } = useQuery({
    queryKey: ["videoProjects", filters, page, pageSize],
    queryFn: () => VideoProjectServices.getAsEditor("id,DESC", filters, page, pageSize),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  const videoProjects = videoProjectsData?.content || [];
  const totalPages = videoProjectsData?.totalPages || 0;
  const totalElements = videoProjectsData?.totalElements || 0;

  const [editorUserIds, setEditorUserIds] = React.useState<number[]>(
    [...new Set([...selectedAssignees, currentUserId])].filter(el => el !== -1)
  );

  React.useEffect(() => {
    if (videoProjects.length > 0) {
      const userIds = videoProjects.flatMap(vp => (vp.assigneeUserId ? [vp.assigneeUserId] : []));
      setEditorUserIds(prevIds => {
        const newIds = [...new Set<number>([...prevIds, ...selectedAssignees, ...userIds])].filter(el => el !== -1);
        return isEqual(prevIds, newIds) ? prevIds : newIds;
      });
    } else if (selectedAssignees.length > 0) {
      setEditorUserIds(prevIds => {
        const newIds = [...new Set<number>([...prevIds, ...selectedAssignees])].filter(el => el !== -1);
        return isEqual(prevIds, newIds) ? prevIds : newIds;
      });
    }
  }, [videoProjects, selectedAssignees, currentUserId]);

  const sortedEditorUserIds = React.useMemo(() => {
    return [...editorUserIds].sort((a, b) => a - b);
  }, [editorUserIds]);

  // Editor query - cached
  const { data: editorsData, isLoading: loadingEditors } = useQuery({
    queryKey: ["editors", sortedEditorUserIds],
    queryFn: () => UsersService.getEditors(sortedEditorUserIds),
    enabled: sortedEditorUserIds.length > 0,
    staleTime: 1000 * 60 * 30, // cache for 30 minutes
  });

  const editors = React.useMemo(() => {
    const rawContent = editorsData?.content || [];
    return [...rawContent].sort((a: DTO.Editor, b: DTO.Editor) => {
      if (a.userId === currentUserId) return -1;
      if (b.userId === currentUserId) return 1;
      if ((a.firstName || "") < (b.firstName || "")) return -1;
      if ((a.firstName || "") > (b.firstName || "")) return 1;
      return 0;
    });
  }, [editorsData, currentUserId]);

  const assigneeOptions = React.useMemo<AssigneeOption[]>(() => {
    return [
      { key: -1, text: "Unassigned", value: -1 },
      ...editors.map(editor => ({
        key: editor.userId ?? -1,
        text: `${editor.firstName} ${editor.lastName}`,
        value: editor.userId ?? -1,
        avatarUrl: editor.photoUrl || Svg.noImageuser,
      })),
    ];
  }, [editors]);

  const loading = loadingVideoProjects || (loadingEditors && editors.length === 0);

  const delayedSetSearchTerm = React.useRef(
    debounce((text: string) => {
      setPage(0);
      setSearchTerm(isEmpty(text) ? null : text);
    }, 500)
  ).current;

  const videoProjectsWithAssigneeNames = (
    videoProjects: DTO.PlayerVideoProjectResponse[],
    editors: DTO.Editor[]
  ): VideoProjectEditorCardRecord[] => {
    return videoProjects.map(videoProject => {
      let assigneeName: string | null = null;
      if (videoProject.assigneeUserId) {
        const editor = editors.find(editor => editor.userId === videoProject.assigneeUserId);
        if (editor) assigneeName = `${editor.firstName} ${editor.lastName}`;
        else assigneeName = "...";
      }

      return {
        ...videoProject,
        assigneeName,
      };
    });
  };

  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    delayedSetSearchTerm(e.target.value);
  };
  const onSelectedStatusesChange = (selectedItems: typeof statusOptions) => {
    const statuses = selectedItems.map(item => item.value);
    localStorage.setItem("editingQueueList-selectedStatuses", statuses.join("|"));
    setPage(0);
    setSelectedStatuses(statuses);
  };
  const onSelectedEditorsChange = (selectedItems: AssigneeOption[]) => {
    const assignees = selectedItems.map(item => item.value);
    localStorage.setItem("editingQueueList-selectedAssignees", assignees.join("|"));
    setPage(0);
    setSelectedAssignees(assignees);
  };
  const onIncludeDemoProjectsChange = (checked: boolean) => {
    localStorage.setItem("includeDemoProjects", `${checked}`);
    setPage(0);
    setIncludeDemoProjects(checked);
  };

  return (
    <PageWrapper title="Editing Queue">
      <div className="flex flex-row flex-wrap items-center gap-3 w-full mb-6">
        <InputGroup
          className="max-w-xs w-full bg-white"
          prepend={<Search className="size-4 text-muted-foreground" />}
          control={controlProps => (
            <Input
              {...controlProps}
              data-elm-id="admin-dashboard-text-search"
              defaultValue={searchTerm || ""}
              placeholder="Search by player or project ID..."
              onChange={onSearchTermChange}
            />
          )}
        />
        <Combobox
          className="max-w-[200px] w-full bg-white"
          placeholder="Select a status"
          multiple
          items={statusOptions}
          value={statusOptions.filter(opt => selectedStatuses.includes(opt.value))}
          onValueChange={onSelectedStatusesChange}
          itemToStringLabel={item => item.text}
          maxChips="auto"
        />
        <Combobox
          className="max-w-[200px] w-full bg-white"
          placeholder="Select an assignee"
          multiple
          items={assigneeOptions}
          value={assigneeOptions.filter(opt => selectedAssignees.includes(opt.value))}
          onValueChange={onSelectedEditorsChange}
          itemToStringLabel={item => item.text}
          itemToIcon={item => {
            if (item.value === -1) return null;
            return (
              <Avatar className="size-5 mr-1.5">
                <Avatar.Image src={item.avatarUrl} />
                <Avatar.Fallback className="text-[8px]">{extractInitials(item.text)}</Avatar.Fallback>
              </Avatar>
            );
          }}
          maxChips="auto"
        />
        <Checkbox
          label="Show test/demo projects"
          checked={includeDemoProjects}
          onCheckedChange={onIncludeDemoProjectsChange}
        />
        {videoProjectsData && (
          <>
            <div className="grow" />
            <Badge variant="outline" size="medium" className="bg-white shadow-xs border-slate-200 py-1.5 px-3">
              <span className="text-slate-500 font-medium">Showing:</span>
              <span className="font-bold text-slate-900 ml-1.5">{totalElements}</span>
            </Badge>
          </>
        )}
      </div>

      <div>
        <div ref={containerRef} data-elm-id="videoProjectListElement" className="flex flex-wrap gap-4">
          {videoProjects.length > 0
            ? videoProjectsWithAssigneeNames(videoProjects, editors).map(videoProject => (
                <VideoProjectEditorCard
                  key={"video_project_card_" + videoProject.videoProjectId}
                  videoProject={videoProject}
                  disabled={loading}
                />
              ))
            : loading &&
              Array.from({ length: pageSize }).map((_, idx) => (
                <VideoProjectEditorCardSkeleton key={"video_project_skeleton_" + idx} />
              ))}
        </div>
        {videoProjects.length === 0 && !loading && (
          <div className="resultsEmptyState flex flex-col items-center justify-center text-center py-12 text-slate-500">
            <Search className="size-12 text-slate-400 mb-3" />
            <h2 className="text-xl font-semibold text-slate-900 mb-1">No results found</h2>
            <div className="text-sm">Adjust your filters and try again</div>
          </div>
        )}
        {videoProjects.length > 0 && (
          <div className="mt-6 flex justify-center w-full">
            <Pagination
              page={page + 1}
              boundaryRange={3}
              totalPages={totalPages}
              disabled={loading}
              onPageChange={p => {
                setPage(p - 1);
              }}
            />
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export const EditorEditingQueueList = withRouter(EditingQueueListFC);
